/** @format */
import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { Observable } from 'rxjs';
import { AuthService, EnvironmentService } from 'sis-common';
import { OrganisationNameService } from '../organisation/organisation-name.service';
import { UniversityService } from '../service/university.service';

type AccessDeniedReason = 'USER_NOT_FOUND' | 'MISSING_ROLE' | 'NO_LOGIN_TIME_WINDOW' | 'USER_LOGIN_DISABLED';

const TRANSLATION_KEY_BY_REASON: Record<AccessDeniedReason, string> = {
    USER_NOT_FOUND: 'NO_SISU_ACCOUNT_PAGE.USER_NOT_FOUND',
    MISSING_ROLE: 'NO_SISU_ACCOUNT_PAGE.MISSING_ROLE',
    NO_LOGIN_TIME_WINDOW: 'NO_SISU_ACCOUNT_PAGE.NO_LOGIN_TIME_WINDOW',
    USER_LOGIN_DISABLED: 'NO_SISU_ACCOUNT_PAGE.USER_LOGIN_DISABLED',
};

const DEFAULT_REASON: AccessDeniedReason = 'USER_NOT_FOUND';

function isAccessDeniedReason(value: unknown): value is AccessDeniedReason {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TRANSLATION_KEY_BY_REASON, value);
}

@Component({
    selector: 'sis-no-sisu-account-page',
    templateUrl: './no-sisu-account-page.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class NoSisuAccountPageComponent implements OnInit {
    universityName$: Observable<string>;
    translationScope: string;
    reason: AccessDeniedReason;

    private loginState: string;

    constructor(
        private universityService: UniversityService,
        private organisationNameService: OrganisationNameService,
        private stateService: StateService,
        private environmentService: EnvironmentService,
        private authService: AuthService,
        private uiRouterGlobals: UIRouterGlobals,
    ) {}

    ngOnInit(): void {
        const appName = this.environmentService.frontendName.toLowerCase();
        this.loginState = `${appName}.login`;

        // Set view fields up front so a one-frame render before state.go does not flash "undefined.TITLE".
        this.reason = isAccessDeniedReason(this.uiRouterGlobals.params?.reason) ? this.uiRouterGlobals.params.reason : DEFAULT_REASON;
        this.translationScope = TRANSLATION_KEY_BY_REASON[this.reason];
        this.universityName$ = this.organisationNameService.getOrgName(this.universityService.getCurrentUniversityOrgId());

        // A logged-in user landing here directly should be sent away — the logout button would otherwise drop a valid session.
        if (this.authService.loggedIn()) {
            this.stateService.go(this.loginState);
        }
    }

    logout(): void {
        this.authService.goToLoginPage(this.reason);
    }
}
